import React from 'react';
import { Badge } from '@/components/common/Badge';

interface JourneyStepSelectorProps {
  selectedSteps: number[];
  onChange: (steps: number[]) => void;
  showPhases?: boolean;
  journeySteps?: Array<{ step: number; name: string; phase: string }>;
}

// Journey step metadata with phases
const journeyStepInfo = [
  { step: 1, name: 'Account & Contact Context Setup', phase: 'Discovery' },
  { step: 2, name: 'Maturity Mirror Exposure', phase: 'Discovery' },
  { step: 3, name: 'Role-Based Pain Framing', phase: 'Discovery' },
  { step: 4, name: 'Maturity Gap Awareness', phase: 'Discovery' },
  { step: 5, name: 'MES Reality Alignment', phase: 'Discovery' },
  { step: 6, name: 'Alignment Call #1', phase: 'Advisory' },
  { step: 7, name: 'Architecture Thinking Introduction', phase: 'Advisory' },
  { step: 8, name: 'Multi-System Coexistence Education', phase: 'Advisory' },
  { step: 9, name: 'Data Readiness Awareness', phase: 'Education' },
  { step: 10, name: 'Automation Timing Guidance', phase: 'Education' },
  { step: 11, name: 'Decision Intelligence Framing', phase: 'Education' },
  { step: 12, name: 'Context Confirmation Call #2', phase: 'Validation' },
  { step: 13, name: 'Future-State Snapshot', phase: 'Validation' },
  { step: 14, name: 'MQL Validation & Sales Handoff', phase: 'Validation' },
];

// Group steps by phase
const stepsByPhase = {
  Discovery: journeyStepInfo.filter(s => s.phase === 'Discovery'),
  Advisory: journeyStepInfo.filter(s => s.phase === 'Advisory'),
  Education: journeyStepInfo.filter(s => s.phase === 'Education'),
  Validation: journeyStepInfo.filter(s => s.phase === 'Validation'),
};

function JourneyStepSelector({ selectedSteps, onChange, showPhases = true }: JourneyStepSelectorProps) {
  const handleStepToggle = (stepNumber: number) => {
    if (selectedSteps.includes(stepNumber)) {
      onChange(selectedSteps.filter(s => s !== stepNumber));
    } else {
      onChange([...selectedSteps, stepNumber].sort((a, b) => a - b));
    }
  };

  const handlePhaseToggle = (phase: keyof typeof stepsByPhase) => {
    const phaseSteps = stepsByPhase[phase].map(s => s.step);
    const allSelected = phaseSteps.every(step => selectedSteps.includes(step));
    
    if (allSelected) {
      // Deselect all phase steps
      onChange(selectedSteps.filter(s => !phaseSteps.includes(s)));
    } else {
      // Select all phase steps
      const newSteps = [...new Set([...selectedSteps, ...phaseSteps])].sort((a, b) => a - b);
      onChange(newSteps);
    }
  };

  if (showPhases) {
    return (
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">
            Journey Steps <span className="text-destructive">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-3">
            Select which journey steps this magnet supports
          </p>
        </div>

        {Object.entries(stepsByPhase).map(([phase, steps]) => {
          const phaseSteps = steps.map(s => s.step);
          const allSelected = phaseSteps.every(step => selectedSteps.includes(step));
          const someSelected = phaseSteps.some(step => selectedSteps.includes(step));

          return (
            <div key={phase} className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id={`phase-${phase}`}
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => handlePhaseToggle(phase as keyof typeof stepsByPhase)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor={`phase-${phase}`} className="font-semibold text-sm cursor-pointer">
                  {phase} Phase
                </label>
                <Badge variant="outline" className="ml-auto text-xs">
                  {phaseSteps.filter(s => selectedSteps.includes(s)).length} / {phaseSteps.length}
                </Badge>
              </div>

              <div className="space-y-2 ml-6">
                {steps.map(({ step, name }) => (
                  <div key={step} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id={`step-${step}`}
                      checked={selectedSteps.includes(step)}
                      onChange={() => handleStepToggle(step)}
                      className="h-4 w-4 rounded border-gray-300 mt-0.5"
                    />
                    <label htmlFor={`step-${step}`} className="text-sm cursor-pointer flex-1">
                      <span className="font-medium">Step {step}:</span> {name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {selectedSteps.length === 0 && (
          <p className="text-xs text-destructive">
            Please select at least one journey step
          </p>
        )}
      </div>
    );
  }

  // Simple list view without phases
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        Journey Steps <span className="text-destructive">*</span>
      </label>
      <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
        {journeyStepInfo.map(({ step, name }) => (
          <div key={step} className="flex items-start gap-2 py-1">
            <input
              type="checkbox"
              id={`step-${step}`}
              checked={selectedSteps.includes(step)}
              onChange={() => handleStepToggle(step)}
              className="h-4 w-4 rounded border-gray-300 mt-0.5"
            />
            <label htmlFor={`step-${step}`} className="text-sm cursor-pointer">
              <span className="font-medium">Step {step}:</span> {name}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

export default JourneyStepSelector;
